import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { Network, Token } from "@/config/tokens"
import { useApi } from "./api"
import { BankAccountGet } from "./bank-accounts"

export enum WithdrawalStatus {
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
  FAILED = "FAILED",
}

export interface Withdrawal {
  amount: number;
  network: Network;
  token: Token;
  bankAccountId: string;
}

export interface WithdrawalGet extends Withdrawal {
  id: string;
  status: WithdrawalStatus;
  createdAt: string;
  updatedAt: string;
  bankAccount: BankAccountGet;
  address: string;
  withdrawalCost: number;
  withdrawalFee: number;
}

export interface Rates {
  us: {
    withdrawalCost: number;
    withdrawalFee: number;
  };
  eu: {
    withdrawalCost: number;
    withdrawalFee: number;
    exchangeRate: number;
  };
}

export function useWithdrawals({ pageSize = 10 }: { pageSize?: number }) {
  const { get } = useApi()

  const query = useInfiniteQuery({
    queryKey: ["withdrawals"],
    queryFn: ({ pageParam = 0 }) => {
      const query = new URLSearchParams()
      query.set("skip", String(pageParam))
      return get(`withdrawals?${query.toString()}&take=10`)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: WithdrawalGet[], allPages) => {
      if (lastPage.length < pageSize) return undefined
      return allPages.length * pageSize
    },
  })

  return {
    withdrawals: query.data?.pages.flat(),
    hasMore: query.hasNextPage,
    isLoading: query.isLoading,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    // Backward compatibility
    size: query.data?.pages.length || 0,
    setSize: (size: number) => {
      if (size > (query.data?.pages.length || 0)) {
        query.fetchNextPage()
      }
    },
    mutate: query.refetch,
  }
}

export function useWithdrawal(id: string) {
  const { get } = useApi()

  const query = useQuery({
    queryKey: ["withdrawal", id],
    queryFn: () => get(`withdrawals/${id}`),
    enabled: !!id,
  })

  return {
    withdrawal: query.data,
    isLoading: query.isLoading,
    error: query.error,
    mutate: query.refetch,
  }
}

export function useWithdrawalsActions() {
  const { post, patch } = useApi()

  return {
    create: (withdrawal: Withdrawal): Promise<WithdrawalGet> => {
      return post("withdrawals", withdrawal)
    },
    update: (id: string, hash: string): Promise<WithdrawalGet> => {
      return patch(`withdrawals/${id}`, { hash })
    },
  }
}

export function useRates() {
  const { get } = useApi()

  const query = useQuery({
    queryKey: ["withdrawal-rates"],
    queryFn: () => get("withdrawals/rates"),
  })

  return {
    rates: query.data,
    isLoading: query.isLoading,
    error: query.error,
  }
}
